import type { Role } from '@/pages/users/types/rbac';
import type { AuditLogRow } from './mapAuditEventToRow';
import { userToAgentReference } from './userToAgentReference';

/** Build FHIR reference → display name from the admin directory. */
export function buildDirectoryNameByReference(
  users: Array<{ id: string; name: string; role: Role }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const user of users) {
    if (!user.id || !user.name) continue;
    map.set(userToAgentReference(user), user.name);
  }
  return map;
}

/** Build Bot/{id} → Bot.name lookup. */
export function buildBotNameByReference(
  bots: Array<{ id?: string; name?: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const bot of bots) {
    if (!bot.id || !bot.name?.trim()) continue;
    map.set(`Bot/${bot.id}`, bot.name.trim());
  }
  return map;
}

/** Merge reference→name maps (later maps win on key collision). */
export function mergeNameByReferenceMaps(
  ...maps: Array<Map<string, string>>
): Map<string, string> {
  const out = new Map<string, string>();
  for (const map of maps) {
    for (const [key, value] of map) {
      out.set(key, value);
    }
  }
  return out;
}

function resolveRefLabel(value: string, nameByRef: Map<string, string>): string {
  return nameByRef.get(value) ?? value;
}

/**
 * Resolve Agent references to directory/bot names.
 * Entity stays as ResourceType/id only — never substitute Patient (or other) names.
 */
export function resolveAuditLogRowNames(
  row: AuditLogRow,
  nameByRef: Map<string, string>,
): AuditLogRow {
  if (nameByRef.size === 0) return row;

  const agentDisplay = resolveRefLabel(row.agentDisplay, nameByRef);
  if (agentDisplay === row.agentDisplay) return row;
  return { ...row, agentDisplay };
}
